import numeral from 'numeraljs'
import moment from 'moment'

import {
  CampaignCodeGeneratorStructure,
  GeneratorFields,
  CampaignCodesCompressed,
  GeneratorSelectFields,
} from '../api-client/api-types'

import { getItemByKeyValue, getValidationCheck } from './index'
import { messages } from '../core/variables'

export function getMetricStatus(date, status, statusIndex): string {
  const dateObject = moment(date)
  const createdLast30Days = moment().diff(dateObject, 'days') <= 30

  let codeType = 'Unused'
  if (status.length === 0) {
    codeType = 'Error'
    if (createdLast30Days) {
      codeType = 'New'
    }
  } else if (statusIndex !== -1) {
    codeType = status[statusIndex]
  } else if (createdLast30Days) {
    codeType = 'New'
  }
  return codeType
}

export function formatMetricValue(value, units): string {
  let formattedNumber = value

  if (typeof value === 'number') {
    if (units === 'percentage') {
      formattedNumber = `${numeral(value).format('0,0')}%`
    } else if (value !== 0) {
      if (units === 'duration') {
        const time = moment.utc(value * 1000).format('mm:ss')
        formattedNumber = `${time}s`
      } else {
        formattedNumber = `${numeral(value).format('0,0.[0]a')}`
      }
    }
  }

  return formattedNumber
}

export function getItemFromArray(arr: any[], index, key): any[] {
  return arr.find((item) => {
    return item[index] && item[index] === key
  })
}

export function getLongLink(lP, tC, separator, prefix): string {
  let div = prefix
  if (lP.indexOf('?') !== -1) {
    div = separator
  }
  return `${lP}${div}${tC}`
}

export function makeLinkSecure(link): string {
  if (
    link &&
    typeof link === 'string' &&
    link.search(/(https:\/\/)|(http:\/\/)/gi) === -1 &&
    link.search(/.\../i) !== -1
  ) {
    return `https://${link}`
  }
  return link
}

export function getAnchorFromString(url: string, masterPrefix = '?'): string {
  const hasAnchor = url.indexOf('#') !== -1
  let useAnchor = ''
  if (hasAnchor) {
    const searchRegEx = new RegExp(`(#.*)[${masterPrefix}]|(#.*)`, 'gi')
    const urlMatch = url.match(searchRegEx)
    if (urlMatch && urlMatch.length > 0) {
      useAnchor = urlMatch[0].replace('?', '')
    }
  }
  return useAnchor
}

interface GeneratedCode {
  pDfs: [string, string][]
  tC: string
  code: string
  url: string
  selected: boolean
}

export default function generateCode(
  urls: string[],
  codeDef: CampaignCodeGeneratorStructure | null,
): GeneratedCode[] {
  if (codeDef === null) return []
  const { paramDefs, masterPrefix, paramSeparator, validationChecks } = codeDef

  const includePrefixWithCopy = getItemByKeyValue(
    validationChecks,
    'name',
    'INCLUDE_PREFIX_WITH_COPY_FROM',
  )
  const includePrefix = includePrefixWithCopy && includePrefixWithCopy.enabled
  const includeEmptyValues = getItemByKeyValue(
    validationChecks,
    'name',
    'INCLUDE_EMPTY_VALUES',
  )
  const includeEmpty = !!(includeEmptyValues && includeEmptyValues.enabled)

  let results: any[] = [[]]

  for (let i = 0; i < paramDefs.length; i += 1) {
    const currentSubArray = paramDefs[i]
    const {
      fieldName,
      fieldType,
      prefix,
      selectFields,
      metaParameter,
      optionValue,
      copyFromField,
      fieldID,
    } = currentSubArray
    const isCopyFromField = copyFromField && copyFromField.length > 0
    const useValue =
      isCopyFromField || !optionValue || optionValue.length === 0
        ? ['']
        : optionValue
    const tempCodes: any[] = []
    for (let j = 0; j < results.length; j += 1) {
      for (let k = 0; k < useValue.length; k += 1) {
        const value = useValue[k]
        if (fieldType === 'input' || !selectFields) {
          tempCodes.push(
            results[j].concat([
              [
                value,
                value,
                metaParameter === true ? '' : `${prefix}${value}`,
                fieldName,
                fieldID,
                copyFromField,
                metaParameter,
                includeEmpty,
                prefix === '',
              ],
            ]),
          )
        } else {
          const found = getItemByKeyValue(selectFields, 'optionValue', value)
          // const useName = !found ? fieldName : found.optionName
          const useName = found === -1 ? '' : found.optionName
          tempCodes.push(
            results[j].concat([
              [
                useName,
                value,
                metaParameter === true ? '' : `${prefix}${value}`,
                fieldName,
                fieldID,
                copyFromField,
                metaParameter,
                includeEmpty,
                prefix === '',
              ],
            ]),
          )
        }
      }
    }

    results = tempCodes
  }

  const copyFromSeparator = getItemByKeyValue(
    validationChecks,
    'name',
    'COPY_FROM_SEPARATOR',
  )
  const useSeparator = copyFromSeparator !== -1 ? copyFromSeparator.value : '|'

  const addCopiedValues = results.map((group) => {
    return group.map((item) => {
      const copyFromField = item[5]
      if (copyFromField) {
        const valuesToCopy = copyFromField
          .map((field, index: number) => {
            const found = getItemFromArray(group, 4, field)
            // get the value with the prefix included
            // first item does not need the prefix
            const res = found
              ? found[index > 0 && includePrefix && found[1] ? 2 : 1]
              : ''
            if (index > 0 && !found[8] && res !== '' && !includePrefix) {
              return `${useSeparator}${res}`
            }
            return res
          })
          .filter((val) => val !== '')
          .join('')
        const copy = [...item]
        copy[0] = item[0] + valuesToCopy
        copy[1] = item[1] + valuesToCopy
        // Exception for meta parameters so they are not included in the code string
        copy[2] = item[6] ? '' : item[2] + valuesToCopy
        return copy
      }
      return [...item]
    })
  })
  let finalResult: any[] = []
  urls.forEach((url) => {
    const addCopiedValuesWithUrl = addCopiedValues.map((item) => {
      const useAnchor = getAnchorFromString(url, masterPrefix)
      const pDfs = item.map((def) => [def[0], def[1], def[3]])
      let tC = item
        .map((def) => {
          // return empty string for empty values, unless includeEmpty is enabled
          return !def[7] && def[0] === '' ? '' : def[2]
        })
        .filter((i) => i !== '')
        .join(paramSeparator)
      tC += useAnchor
      const hasPrefix = url.indexOf('?') !== -1
      const isPrefixLastCharacter =
        hasPrefix && url.indexOf('?') === url.length - 1
      const prefixToReplace = isPrefixLastCharacter ? '' : '&'
      const useUrl = url.replace(useAnchor, '')
      const code = `${useUrl}${
        hasPrefix ? masterPrefix.replace('?', prefixToReplace) : masterPrefix
      }${tC}`

      return {
        url: useUrl,
        pDfs,
        tC,
        code,
        selected: true,
      }
    })

    finalResult = [...finalResult, ...addCopiedValuesWithUrl]
  })
  return finalResult
}

export function isFieldAChild(field): boolean {
  const { selectFields } = field
  if (selectFields) {
    const haveParents = selectFields
      .filter((item) => !item.hide)
      .filter((item) => {
        const { optionFilter } = item
        if (optionFilter && optionFilter.length > 0) {
          const { parentOptionIDs } = optionFilter[0]
          return !!(parentOptionIDs && parentOptionIDs.length > 0)
        }
        return false
      })
    return !!(haveParents.length > 0)
  }
  return false
}

export function isFieldAParent(form, field): boolean {
  const { paramDefs } = form
  const { fieldID } = field

  const allOtherParamDefs = paramDefs.filter(
    (item) => !!(item.fieldID !== fieldID && item.selectFields),
  )

  if (allOtherParamDefs.length > 0) {
    const haveChildren = allOtherParamDefs.filter((item) => {
      const { selectFields } = item
      if (selectFields) {
        const selected = selectFields
          .filter((i) => !i.hide)
          .filter((selectField) => {
            const { optionFilter } = selectField
            if (optionFilter && optionFilter.length > 0) {
              const { parentFieldID, parentOptionIDs } = optionFilter[0]
              return !!(
                parentOptionIDs &&
                parentOptionIDs.length > 0 &&
                parentFieldID === fieldID
              )
            }
            return false
          })
        return !!(selected.length > 0)
      }
      return false
    })
    return !!(haveChildren.length > 0)
  }
  return false
}

export function getChildrenValues(field, parent): string[] {
  const { selectFields } = field
  const { optionID: parentOptionId } = parent
  const result: string[] = []
  if (selectFields && selectFields.length > 0) {
    selectFields.forEach((item) => {
      const { optionFilter, optionID } = item
      if (optionFilter && optionFilter.length > 0) {
        const { parentOptionIDs } = optionFilter[0]
        if (parentOptionIDs && parentOptionIDs.length > 0) {
          if (parentOptionIDs.indexOf(parentOptionId) !== -1) {
            result.push(optionID)
          }
        }
      }
    })
  }
  return result
}

export function getParentValue(form, field): any[] {
  const { paramDefs } = form
  const { fieldID, selectFields } = field
  let selectedParentOptions: any[] = []
  const parentIds: string[] = []

  const allOtherParamDefs = paramDefs.filter(
    (item) =>
      !!(
        item.fieldID !== fieldID &&
        item.selectFields &&
        item.optionValue &&
        item.optionValue.length > 0 &&
        item.optionValue[0] !== ''
      ),
  )

  if (allOtherParamDefs.length > 0 && selectFields) {
    selectFields
      .filter((i) => !i.hide)
      .forEach((item) => {
        const { optionFilter } = item
        if (optionFilter && optionFilter.length > 0) {
          const { parentOptionIDs, parentFieldID } = optionFilter[0]
          if (parentOptionIDs && parentOptionIDs.length > 0) {
            if (parentIds.indexOf(parentFieldID) === -1) {
              parentIds.push(parentFieldID)
            }
          }
        }
      })
    const parents = allOtherParamDefs.filter(
      (i) => parentIds.indexOf(i.fieldID) !== -1,
    )
    parents.forEach((parent) => {
      const {
        selectFields: parentSelectFields,
        optionValue,
        fieldName,
      } = parent
      if (parentSelectFields) {
        selectedParentOptions = parentSelectFields.filter(
          (i) => optionValue.indexOf(i.optionValue) !== -1,
        )
        selectedParentOptions = selectedParentOptions.map((i) => {
          return {
            ...i,
            useLabel: `${fieldName}: ${i.optionName}`,
          }
        })
      }
    })
  }
  return selectedParentOptions
}

export function getChildValue(form, field): any[] {
  const { paramDefs } = form
  const { fieldID } = field
  let selectedChildOptions: any[] = []

  const allOtherParamDefs = paramDefs.filter(
    (item) =>
      !!(
        item.fieldID !== fieldID &&
        item.selectFields &&
        item.optionValue &&
        item.optionValue.length > 0 &&
        item.optionValue[0] !== ''
      ),
  )

  if (allOtherParamDefs.length > 0) {
    allOtherParamDefs.forEach((paramDef) => {
      const { selectFields, optionValue } = paramDef
      if (selectFields) {
        selectedChildOptions = [
          ...selectedChildOptions,
          ...selectFields.filter(
            (i) => !i.hide && optionValue.indexOf(i.optionValue) !== -1,
          ),
        ]
      }
    })
  }

  return selectedChildOptions.filter((item) => {
    const { optionFilter } = item
    if (optionFilter && optionFilter.length > 0) {
      const { parentFieldID, parentOptionIDs } = optionFilter[0]
      return (
        parentFieldID === fieldID &&
        parentOptionIDs &&
        parentOptionIDs.length > 0
      )
    }
    return false
  })
}

export function filterByParentValue(filtered, parentValue): any[] {
  const parentValueOptionIDs = parentValue.map((i) => i.optionID)
  const result = filtered.filter((item) => {
    const { optionFilter } = item
    if (optionFilter && optionFilter.length > 0) {
      const { parentOptionIDs } = optionFilter[0]
      if (parentOptionIDs && parentOptionIDs.length > 0) {
        const containsParent = parentOptionIDs.filter(
          (i) => parentValueOptionIDs.indexOf(i) !== -1,
        )
        return containsParent.length > 0
      }
    }
    return true
  })
  return result
}

export function filterByChildValue(filtered, childValue): any[] {
  let permittedParentOptionIDs: string[] = []
  childValue.forEach((item) => {
    const { optionFilter } = item
    if (optionFilter && optionFilter.length > 0) {
      const { parentOptionIDs } = optionFilter[0]
      if (parentOptionIDs && parentOptionIDs.length > 0) {
        permittedParentOptionIDs = [
          ...permittedParentOptionIDs,
          ...parentOptionIDs.filter(
            (i) => permittedParentOptionIDs.indexOf(i) === -1,
          ),
        ]
      }
    }
  })
  const result = filtered.filter((item) => {
    return permittedParentOptionIDs.indexOf(item.optionID) !== -1
  })
  return result
}

export function filterList(form, field, single: boolean) {
  let filtered = field.selectFields.filter((item) => !item.hide)

  const isChild = isFieldAChild(field)
  const isParent = isFieldAParent(form, field)

  if (isChild) {
    const parentValue = getParentValue(form, field)

    if (parentValue.length > 0) {
      // filter by parent value
      filtered = filterByParentValue(filtered, parentValue)
    }
    return filtered
  }

  // For multi journey we need to exclude this filter
  if (isParent && single) {
    const childValue = getChildValue(form, field)
    if (childValue.length > 0) {
      // filter by child value
      filtered = filterByChildValue(filtered, childValue)
    }
  }

  return filtered
}

interface GetCsvHeaderProps {
  metrics: MetricSource[]
  selectedMetrics: string[]
  generatedStructure: CampaignCodeGeneratorStructure
}

export function getCsvHeader({
  metrics,
  selectedMetrics,
  generatedStructure,
}: GetCsvHeaderProps) {
  const result: string[] = []
  result.push('User')
  result.push('Timestamp')
  result.push('Short link')
  result.push('Landing page with campaign code')
  result.push('Status')

  selectedMetrics.forEach((item, index) => {
    const useMetric = getItemByKeyValue(metrics, 'metricID', item)
    if (useMetric !== -1) {
      result.push(useMetric.displayName)
    }
  })

  generatedStructure.paramDefs.forEach((field: GeneratorFields) => {
    result.push(field.fieldName)
  })

  return result.map((r) => `"${r.replace(/\"/gi, '""')}"`) // escape quotes
}

interface GetCsvRowsProps {
  selectedCodes: string[]
  codes: CampaignCodesCompressed[]
  selectedMetrics: string[]
  generatedStructure: CampaignCodeGeneratorStructure
  selectedMetricsIndexes: number[]
  metricValues: any
  codeIDs?: string[]
  status?: string[]
}

export function getCsvRows({
  selectedCodes,
  codes,
  selectedMetrics,
  generatedStructure,
  selectedMetricsIndexes,
  metricValues,
  codeIDs = [],
  status = [],
}: GetCsvRowsProps) {
  let result: string[][] = []
  const getCode = (code) => {
    const row: string[] = []
    row.push(code.e)
    row.push(code.t)
    row.push(code.sL ? `https://upl.inc/${code.sL}` : '')
    row.push(code.c)

    const codeIdIndex = codeIDs.indexOf(code.cID)
    row.push(getMetricStatus(code.t, status, codeIdIndex))

    selectedMetricsIndexes.forEach((selectedMetricsIndex) => {
      if (codeIdIndex !== -1) {
        const metric = metricValues[selectedMetricsIndex]
        const { mf365, units } = metric
        const value = mf365[codeIdIndex]
        row.push(`${formatMetricValue(value, units)}`)
      } else {
        row.push('')
      }
    })

    generatedStructure.paramDefs.forEach((paramDef: any) => {
      const codeDef = getItemByKeyValue(code.cDef, 'fID', paramDef.fieldID)
      if (codeDef !== -1) {
        row.push(codeDef.n)
      } else {
        row.push('')
      }
    })

    return row.map((r) => `"${r.replace(/\"/gi, '""')}"`) // escape quotes
  }

  if (selectedCodes.length === 0) {
    result = codes.map((code) => getCode(code))
  } else {
    codes.forEach((c) => {
      if (c.cID && selectedCodes.indexOf(c.cID) !== -1) {
        result.push(getCode(c))
      }
    })
  }
  return result
}

interface GetCsvStringProps {
  metrics?: MetricSource[]
  selectedMetrics?: string[]
  selectedMetricsIndexes?: number[]
  selectedCodes: string[]
  codes: CampaignCodesCompressed[]
  generatedStructure: CampaignCodeGeneratorStructure
  storedCodeStats?: any
}

export function getCsvString({
  metrics = [],
  selectedMetrics = [],
  selectedMetricsIndexes = [],
  selectedCodes,
  codes,
  generatedStructure,
  storedCodeStats = { metricValues: [], codeIDs: [], status: [] },
}: GetCsvStringProps): string {
  const { metricValues, codeIDs, status } = storedCodeStats
  const csvRows: string[] = []
  csvRows.push(
    getCsvHeader({
      metrics,
      selectedMetrics,
      generatedStructure,
    }).join(','),
  )
  const rows = getCsvRows({
    selectedMetricsIndexes,
    metricValues,
    selectedCodes,
    codes,
    codeIDs,
    status,
    selectedMetrics,
    generatedStructure,
  })
  rows.reverse().forEach((row) => {
    csvRows.push(row.join(','))
  })
  return csvRows.join('\n')
}

export function countCombinations(
  codeDef: CampaignCodeGeneratorStructure | null,
  url = [''],
): number {
  // const codes = generateCode(url, codeDef)
  // return codes.length

  if (codeDef === null) return 0
  const { paramDefs } = codeDef

  let counter = 1
  paramDefs.forEach((item) => {
    if (
      item.optionValue &&
      item.optionValue.length > 0 &&
      item.fieldType === 'select'
    ) {
      counter *= item.optionValue.length
    }
    // return counter
  })

  return counter * url.length
}

export function checkParamNameIsInvalid(
  name: string,
  allParams: string[],
  callback?: any,
  currName?: string,
): boolean {
  if (name === '') {
    if (callback) {
      callback(true)
    }
    return true
  }

  if (allParams.length > 0) {
    const paramExists =
      allParams.filter((param) => {
        return param === name && (!currName || param !== currName)
      }).length > 0
    if (callback) {
      callback(paramExists)
    }
    return paramExists
  }
  return false
}

export function getValidationStatus(
  recentlyValidatedUrls,
  url,
  validationChecks: any[] = [],
) {
  const urlSpeed = getValidationCheck(validationChecks, 'CHECK_URL_SPEED', true)
    .enabled
  const urlRedirect = getValidationCheck(
    validationChecks,
    'CHECK_URL_REDIRECT',
    true,
  ).enabled
  const urlAnalytics = getValidationCheck(
    validationChecks,
    'CHECK_URL_ANALYTICS',
    true,
  ).enabled
  const urlExists = getValidationCheck(
    validationChecks,
    'CHECK_URL_EXISTS',
    true,
  ).enabled

  let result: any[] = []

  if (Object.prototype.hasOwnProperty.call(recentlyValidatedUrls, url)) {
    // eslint-disable-next-line
    // debugger
    const validatedUrl = recentlyValidatedUrls[url]
    if (!validatedUrl) {
      result.push({ status: 'validating', message: 'N/A' })
    }

    if (Object.prototype.hasOwnProperty.call(validatedUrl, 'statusCode')) {
      if (
        urlExists &&
        validatedUrl.statusCode >= 200 &&
        validatedUrl.statusCode <= 299
      ) {
        result.push({
          status: 'valid',
          message: 'This page exists.',
        })
      }
      if (
        urlRedirect &&
        validatedUrl.statusCode >= 300 &&
        validatedUrl.statusCode <= 399
      ) {
        result.push({
          status: 'warning',
          message: 'This page is redirecting.',
        })
        return result
      }
      if (
        validatedUrl.statusCode === 0 ||
        (validatedUrl.statusCode >= 400 && validatedUrl.statusCode <= 599)
      ) {
        result.push({
          status: 'invalid',
          message: messages.urlStatus.badUrl,
        })
        return result
      }

      if (Object.keys(validatedUrl).length === 1) {
        return result
      }
    }

    const {
      badUrl,
      noAnalyticsTag,
      redirectedLandingPage,
      slowLandingPage,
    } = validatedUrl

    if (badUrl) {
      result.push({
        status: 'invalid',
        message: messages.urlStatus.badUrl,
      })
      return result
    }
    if (
      (urlAnalytics && noAnalyticsTag) ||
      (urlRedirect && redirectedLandingPage) ||
      (urlSpeed && slowLandingPage)
    ) {
      if (urlSpeed && slowLandingPage) {
        result.push({
          status: 'invalid',
          message: messages.urlStatus.slowLandingPage,
        })
        return result
      }
      if (urlRedirect && redirectedLandingPage) {
        result.push({
          status: 'invalid',
          message: messages.urlStatus.redirectedLandingPage,
        })
        return result
      }
      if (urlAnalytics) {
        result.push({
          status: 'invalid',
          message: messages.urlStatus.invalid,
        })
      }
      return result
    }
    if (!badUrl) {
      // valid: 'Valid page (live, fast, has analytics and no redirects).',
      const checksLabels = {
        urlExists: 'live',
        urlSpeed: 'is fast',
        urlRedirect: 'no redirects',
        urlAnalytics: 'has analytics',
      }
      const useChecks: string[] = []
      // if (urlExists) {
      //   useChecks.push(checksLabels.urlExists)
      // }
      if (urlSpeed) {
        useChecks.push(checksLabels.urlSpeed)
      }
      if (urlAnalytics) {
        useChecks.push(checksLabels.urlAnalytics)
      }
      if (urlRedirect) {
        useChecks.push(checksLabels.urlRedirect)
      }

      const summary = useChecks.join(', ')
      let summaryString = summary.length > 0 ? ` (${summary})` : ''
      if (useChecks.length > 1) {
        const ender = useChecks.pop()
        summaryString = `, ${useChecks.join(', ')} and ${ender}`
      }
      // result.push({
      //   status: 'valid',
      //   message: `Valid page${summaryString}.`,
      // })
      result = [
        {
          status: 'valid',
          message: `This page exists${summaryString}.`,
        },
      ]
      return result
    }
  }
  return null
}

// fields validation
export function isAllFormDataValid(data: CampaignCodeGeneratorStructure) {
  // Find all empty values that should not be empty
  const emptyValues = { ...data }.paramDefs.filter((item) => {
    const { fieldType, optionValue, required } = item

    if (required) {
      if (typeof optionValue !== 'undefined' && optionValue.length > 0) {
        if (fieldType === 'select' && item.selectFields) {
          const fieldsThatExist = optionValue.filter((val: string) => {
            if (item.selectFields) {
              return item.selectFields.some(
                (i: GeneratorSelectFields): boolean => i.optionValue === val,
              )
            }
            return false
          })
          return fieldsThatExist.length !== optionValue.length
        }
        return (
          (fieldType === 'input' || fieldType === 'date') &&
          optionValue.join('') === ''
        )
      }
      return true
    }
    return false
  })
  return emptyValues.length === 0
}
